import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useReadableOnPrimary } from '../hooks/useReadableOnPrimary';

/**
 * "Quality at the Core / Smarter, More Sustainable Building" — two
 * alternating image/text rows for the Better-Built Living page.
 *
 * Each row has: image + title + body. Rows alternate white and
 * primary-tinted backgrounds and swap which column holds the image.
 *
 * NOTE: Each row uses literal `groupPath`/`path` values and inline literal
 * slot group definitions so the schema linter can statically extract them.
 */

const row1ImageDef: SlotGroupDefinition = {
  label: 'Quality Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'property_featured_image_media.alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A photo that showcases polished build quality, modern finishes, or a well-crafted interior detail at this specific property — a kitchen countertop, floor-to-ceiling window, or custom cabinetry close-up that reads "premium construction".',
    context:
      'Row 1 of 2 (Quality at the Core) on the Better-Built Living page. Keep distinct from row 2.',
  },
};

const row2ImageDef: SlotGroupDefinition = {
  label: 'Sustainable Building Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'property_featured_image_media.alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A photo that evokes smart, sustainable building for this specific property — a daylit interior, modern energy-efficient windows, a landscaped exterior with green elements, or a clean exterior architectural shot that reads "thoughtful design".',
    context:
      'Row 2 of 2 (Smarter, More Sustainable Building) on the Better-Built Living page. Keep distinct from row 1.',
  },
};

export function BetterBuiltFeatures() {
  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 160 });
  const readableOnPrimary = useReadableOnPrimary();

  const img1 = useSlotGroup('betterbuiltfeatures.row1_image', row1ImageDef);
  const img2 = useSlotGroup('betterbuiltfeatures.row2_image', row2ImageDef);

  return (
    <section className="w-full">
      <div ref={gridRef}>
        {/* Row 1 — WHITE band — image left, copy right — Quality at the Core */}
        <div data-reveal className="reveal-fade-up bg-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
              <div className="relative aspect-[4/3] md:aspect-[5/4] overflow-hidden">
                <EditableSlotGroup groupPath="betterbuiltfeatures.row1_image" definition={row1ImageDef}>
                  {img1.image ? (
                    <VibesitesImage
                      url={img1.image}
                      alt={img1.alt || ''}
                      sizes="(min-width: 768px) 50vw, 100vw"
                      className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                  )}
                </EditableSlotGroup>
              </div>
              <div className="md:pl-4">
                <RichTextContent
                  path="betterbuiltfeatures.row1_title"
                  defaultValue="<p>Quality at the Core</p>"
                  as="h3"
                  className="font-heading text-2xl sm:text-3xl md:text-4xl font-normal text-[var(--vibe-color-text)] leading-tight tracking-tight mb-4"
                />
                <RichTextContent
                  path="betterbuiltfeatures.row1_body"
                  defaultValue="<p>For us, quality is a foundation, not just a feature. With American-made steel at the heart of our homes, we deliver unmatched strength, durability, and soundproofing. These qualities create solid, quieter spaces where you can thrive.</p>"
                  as="div"
                  className="font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-3 last:[&_p]:mb-0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Row 2 — PRIMARY band — copy left, image right — Smarter, More Sustainable Building */}
        <div data-reveal style={readableOnPrimary} className="reveal-fade-up bg-[var(--vibe-color-primary)] py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
              <div className="relative aspect-[4/3] md:aspect-[5/4] overflow-hidden md:order-2">
                <EditableSlotGroup groupPath="betterbuiltfeatures.row2_image" definition={row2ImageDef}>
                  {img2.image ? (
                    <VibesitesImage
                      url={img2.image}
                      alt={img2.alt || ''}
                      sizes="(min-width: 768px) 50vw, 100vw"
                      className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-[color-mix(in_srgb,var(--vibe-color-primary)_75%,black)]" />
                  )}
                </EditableSlotGroup>
              </div>
              <div className="md:order-1 md:pr-4">
                <RichTextContent
                  path="betterbuiltfeatures.row2_title"
                  defaultValue="<p>Smarter, More Sustainable Building</p>"
                  as="h3"
                  className="font-heading text-2xl sm:text-3xl md:text-4xl font-normal text-[var(--vibe-color-text-on-primary)] leading-tight tracking-tight mb-4"
                />
                <RichTextContent
                  path="betterbuiltfeatures.row2_body"
                  defaultValue="<p>Our off-site construction process values precision, efficiency, and sustainability. By designing and building in a controlled environment, we ensure every component meets rigorous standards. This approach reduces waste, shortens build times, and results in homes that are better for you and the environment.</p>"
                  as="div"
                  className="font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_p]:mb-3 last:[&_p]:mb-0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'BetterBuiltFeatures',
  displayName: 'Better-Built Features',
  tokens: {
    'betterbuiltfeatures.row1_title': {
      label: 'Row 1 Title',
      type: 'richtext',
      defaultValue: '<p>Quality at the Core</p>',
    },
    'betterbuiltfeatures.row1_body': {
      label: 'Row 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>For us, quality is a foundation, not just a feature. With American-made steel at the heart of our homes, we deliver unmatched strength, durability, and soundproofing. These qualities create solid, quieter spaces where you can thrive.</p>',
    },
    'betterbuiltfeatures.row2_title': {
      label: 'Row 2 Title',
      type: 'richtext',
      defaultValue: '<p>Smarter, More Sustainable Building</p>',
    },
    'betterbuiltfeatures.row2_body': {
      label: 'Row 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Our off-site construction process values precision, efficiency, and sustainability. By designing and building in a controlled environment, we ensure every component meets rigorous standards. This approach reduces waste, shortens build times, and results in homes that are better for you and the environment.</p>',
    },
  },
  slotGroups: {
    'betterbuiltfeatures.row1_image': row1ImageDef,
    'betterbuiltfeatures.row2_image': row2ImageDef,
  },
};

(BetterBuiltFeatures as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default BetterBuiltFeatures;
